import { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import type { AuthStatus, AuthUser, DvmsRole } from './types';
import { getOktaAuth } from './okta';
import { setCurrentAuthor } from '../state/dvmsStore';
import { clearSysConfigCache } from '../hooks/useSysConfigs';

interface AuthContextValue {
    status: AuthStatus;
    user: AuthUser | null;
    /** Triggers Okta redirect for login. */
    login: () => void;
    logout: () => void;
    /** Handle the OIDC callback (exchange code for tokens). */
    handleCallback: () => Promise<void>;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const OKTA_ORIGINAL_URI_KEY = 'okta-original-uri';

// ── Okta helpers ────────────────────────────────────────────────────────────

/**
 * Maps Okta groups claim to DVMS roles.
 * Patterns are checked case-insensitively against the group name.
 * Add new naming conventions here — don't scatter them across the codebase.
 */
const GROUP_ROLE_MAP: { substrings?: string[]; exact?: string[]; role: DvmsRole }[] = [
    {
        substrings: ['dvms_admin', 'dvms-admin'],
        exact: ['dlx.dvms.super-admin.group'],
        role: 'admin',
    },
    {
        substrings: ['dvms_project_admin', 'dvms-project-admin'],
        exact: ['dlx.dvms.project-admin.group'],
        role: 'project_admin',
    },
    {
        substrings: ['dlx.dvms.'],
        role: 'project_reader',
    },
];

function mapGroupsToRoles(groups: string[] | undefined): DvmsRole[] {
    if (!groups || groups.length === 0) {
        return ['project_reader'];
    }

    const roles = new Set<DvmsRole>();

    for (const g of groups) {
        const lower = g.toLowerCase();
        for (const entry of GROUP_ROLE_MAP) {
            const matchesSubstring = entry.substrings?.some((s) => lower.includes(s));
            const matchesExact = entry.exact?.some((e) => lower === e.toLowerCase());
            if (matchesSubstring || matchesExact) {
                roles.add(entry.role);
                break;
            }
        }
    }

    return roles.size > 0 ? [...roles] : ['project_reader'];
}

function asClaimString(v: unknown): string {
    return typeof v === 'string' ? v : '';
}

function buildUserFromOktaClaims(claims: Record<string, unknown>): AuthUser {
    const groups = Array.isArray(claims.groups) ? (claims.groups as string[]) : undefined;
    const email = asClaimString(claims.email);
    const name = asClaimString(claims.name);
    return {
        id: asClaimString(claims.sub),
        email,
        name: name !== '' ? name : email,
        roles: mapGroupsToRoles(groups),
    };
}

// ── Provider ────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [status, setStatus] = useState<AuthStatus>('loading');
    const [user, setUser] = useState<AuthUser | null>(null);

    // Keep the store's audit-trail author in sync with the current user
    useEffect(() => {
        setCurrentAuthor(user?.name ?? '');
    }, [user]);

    // On mount: check for existing session (skip on login callback — handleCallback will run instead)
    useEffect(() => {
        if (window.location.pathname.includes('/login/callback')) {
            return;
        }
        void (async () => {
            try {
                const oktaAuth = getOktaAuth();
                const isAuthenticated = await oktaAuth.isAuthenticated();
                if (isAuthenticated) {
                    const userInfo = await oktaAuth.getUser();
                    setUser(buildUserFromOktaClaims(userInfo));
                    setStatus('authenticated');
                } else {
                    setStatus('unauthenticated');
                }
            } catch {
                setStatus('unauthenticated');
            }
        })();
    }, []);

    const login = useCallback(() => {
        sessionStorage.setItem(OKTA_ORIGINAL_URI_KEY, window.location.pathname);
        const oktaAuth = getOktaAuth();
        void oktaAuth.signInWithRedirect({ originalUri: window.location.pathname });
    }, []);

    const handleCallback = useCallback(async () => {
        const oktaAuth = getOktaAuth();
        await oktaAuth.storeTokensFromRedirect();
        clearSysConfigCache(); // new session — force fresh configs
        const userInfo = await oktaAuth.getUser();
        setUser(buildUserFromOktaClaims(userInfo));
        setStatus('authenticated');
    }, []);

    const logout = useCallback(() => {
        const oktaAuth = getOktaAuth();
        clearSysConfigCache();
        void oktaAuth.signOut();
        setUser(null);
        setStatus('unauthenticated');
    }, []);

    const refresh = useCallback(async () => {
        const oktaAuth = getOktaAuth();
        try {
            await oktaAuth.tokenManager.renew('accessToken');
            const userInfo = await oktaAuth.getUser();
            setUser(buildUserFromOktaClaims(userInfo));
            setStatus('authenticated');
        } catch {
            setStatus('unauthenticated');
            setUser(null);
        }
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({ status, user, login, logout, handleCallback, refresh }),
        [status, user, login, logout, handleCallback, refresh],
    );

    return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}
